import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton, Link, Stack,
    Typography
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useEffect, useState } from "react";
import Papa, { ParseError, ParseResult } from 'papaparse';
import { useParticipantCheckMutation } from "../../../../api/dataWranglerApiSlice";
import { useDispatch } from "react-redux";
import { setParticipantFilter } from "../../dataWranglerSlice";
import { Cancel, UploadFile } from "@mui/icons-material";
import { colours } from "../../../../StyleConstants";
import File from "./File";

interface Props {
    open: boolean;
    close: () => void;
}

const UploadParticipantFileModal = (props: Props) => {
    const { acceptedFiles, getRootProps, getInputProps } = useDropzone();
    const [filePresent, setFilePresent] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [filterParticipants, setFilterParticipants] = useState<string[]>([]);
    const [triggerCheck] = useParticipantCheckMutation();
    const dispatch = useDispatch();

    const onClose = () => {
        setErrors([]);
        setWarnings([]);
        setFilterParticipants([]);
        setFilePresent(false);
        props.close();
    }

    const parseData = async (results: ParseResult<{[key: string]: string}>) => {
        if (results.errors.length > 0) {
            setErrors(results.errors.map((error: ParseError) => error.message))
        } else if (results.data.length === 0) {
            setErrors(["File is empty"])
        } else {
            const warnings: string[] = [];
            const participants: {[key: string]: boolean} = {};
            let noneUnique = false;
            if (!Object.keys(results.data[0]).includes("participant_id")) {
                setErrors(["Missing the participant_id column."]);
                return;
            }
            if (Object.keys(results.data[0]).length > 1) {
                warnings.push("Additional columns detected, these will be ignored.")
            }
            results.data.forEach((row: {[key: string]: string}) => {
                if (!participants[row['participant_id']]) {
                    participants[row['participant_id']] = true;
                } else {
                    noneUnique = true;
                }
            })
            if (noneUnique) {
                warnings.push(`Duplicate participant_ids found, only ${Object.keys(participants).length} participants found`)
            }
            const unknown = await triggerCheck(Object.keys(participants));
            if (unknown.data.length > 0) {
                warnings.push(`Unknown participants found in csv: ${unknown.data.length} unknown`);
                unknown.data.forEach((participant: string) => {
                    delete participants[participant];
                })
            }
            setFilterParticipants(Object.keys(participants));
            setWarnings(warnings);
        }
    }

    useEffect(() => {
        if (acceptedFiles.length === 1) {
            setFilePresent(true);
            if (acceptedFiles[0].type !== 'text/csv') {
                setErrors([`Unsupported File Type: ${acceptedFiles[0].type}`]);
            } else {
                setErrors([]);
                Papa.parse(acceptedFiles[0] as File, {
                    delimiter: ",",
                    header: true,
                    skipEmptyLines: true,
                    complete: parseData
                });
            }
        } else if (acceptedFiles.length > 0) {
            setFilePresent(true);
            setErrors(["Multiple Files Uploaded, only support 1 file."]);
        }
    }, [acceptedFiles]);

    const applyParticipantFilter = () => {
        dispatch(setParticipantFilter(filterParticipants));
        onClose();
    }

    const removeFile = () => {
        setFilePresent(false);
        setErrors([]);
        setWarnings([]);
        setFilterParticipants([]);
    }

    const downloadTemplateCSV = () => {
        // Create CSV content with just the header
        const csvContent = "participant_id\n";

        // Create a Blob with the CSV content
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        // Create a URL for the Blob
        const url = URL.createObjectURL(blob);

        // Create a temporary link element
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'participant_filter_template.csv');

        // Append the link to the body
        document.body.appendChild(link);

        // Trigger the download
        link.click();

        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    return (
        <Dialog
            open={props.open}
            onClose={onClose}
            fullWidth={true}
            maxWidth={'md'}
        >
            <DialogTitle>
                <Typography fontSize="32px" fontWeight={600}>
                    Upload Participant Filter
                </Typography>
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
                <Cancel />
            </IconButton>
            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '32px',
                    alignItems: 'center'
                }}
            >
                {filePresent
                    ? (
                        <File
                            removeFile={removeFile}
                            participantCount={filterParticipants.length}
                            errors={errors}
                            warnings={warnings}
                        />
                    ) : (
                        <Stack
                            sx={{
                                gap: '32px'
                            }}
                        >
                            {errors.map((error: string, i: number) => (
                                <Alert key={`error-${i}`} severity="error">{error}</Alert>
                            ))}
                            {warnings.map((warning: string, i: number) => (
                                <Alert key={`warning-${i}`} severity="warning">{warning}</Alert>
                            ))}
                            <Typography fontSize="18px" fontWeight={400}>
                                Upload a CSV file with a participant list to filter the data.
                                Correct formatting is 1 column header: <strong>participant ID;</strong>  format of participant ID’s: <strong>sub-#########</strong>.
                                For more information on naming conventions see: <a href={"https://docs.abcdstudy.org/latest/documentation/curation/standards.html#identifier-columns"}>Curation standards</a>.  If you are unsure of the CSV formatting you can download a template below.
                            </Typography>
                            <Box
                                {...getRootProps({ className: 'dropzone' })}
                                component="section"
                                sx={{
                                    px: '16px',
                                    py: '24px',
                                    border: '1px dashed grey',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: "16px"
                                }}
                            >
                                <input {...getInputProps()} />
                                <UploadFile fontSize="large" sx={{ color: colours.orange.orange }} />
                                <Typography variant={"h5"}>
                                    <Link sx={{ color: colours.orange.orange }} href={"#"}>Click to upload</Link> or Drag and Drop
                                </Typography>
                                <Typography variant={"body2"}>
                                    CSV files only
                                </Typography>
                            </Box>
                        </Stack>
                    )}
                {filterParticipants.length > 0 ? (
                    <>
                        <Typography fontSize="18px" fontWeight={400}>
                            {`You are about to filter ${filterParticipants.length} participants.`}
                        </Typography>
                        <Button
                            variant="primary"
                            onClick={applyParticipantFilter}
                        >
                            Filter Participants
                        </Button>
                    </>
                ) : (
                    <Button
                        variant="secondary"
                        onClick={downloadTemplateCSV}
                    >
                        Download Template CSV
                    </Button>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default UploadParticipantFileModal;
